<template>
  <div class="container-fluid py-1">
    <div class="row">
      <div class="col-12">
        <div class="card my-4">
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
            <div class="bg-gradient-success shadow-success border-radius-lg pt-3 pb-3 px-3">
              <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                  <h6 class="text-white text-capitalize mb-1">Licenses</h6>
                  <p class="text-white text-sm mb-0 opacity-8">
                    Manage your licenses and submit requests for Board approval.
                  </p>
                </div>
                <button class="btn btn-light btn-sm mb-0" @click="openCreateModal">
                  <i class="fas fa-plus me-2"></i>
                  Register New License
                </button>
              </div>
            </div>
          </div>

          <div class="card-body px-4 pb-4">
            <div class="row g-3 mb-3">
              <div class="col-md-8">
                <label class="form-label text-xs text-secondary mb-1">Search</label>
                <div class="filter-input-wrapper">
                  <input class="form-control form-control-modern" v-model.trim="searchQuery"
                    placeholder="Search by licence type, ID, or status" />
                  <i class="fas fa-search search-icon"></i>
                </div>
              </div>
              <div class="col-md-2">
                <label class="form-label text-xs text-secondary mb-1">Status</label>
                <select class="form-control form-control-modern" v-model="statusFilter">
                  <option value="all">All</option>
                  <option value="pending">Pending Review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div class="col-md-2">
                <button class="btn btn-outline-warning btn-strong-orange w-100 mb-0 mt-4" @click="resetFilters">
                  <i class="fas fa-times me-2"></i>
                  Clear
                </button>
              </div>
            </div>

            <div class="table-responsive p-0">
              <div v-if="isLoading" class="text-center py-5">
                <div class="spinner-border text-success" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-3 text-muted">Loading licenses...</p>
              </div>

              <div v-else-if="paginatedRows.length === 0" class="text-center py-5">
                <i class="fas fa-id-card fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No licenses found</h5>
                <p class="text-sm text-muted mb-1">
                  The licenses you register here determine the reports you can configure.
                </p>
                <p class="text-sm text-muted mb-0">
                  To get your license details, please log into your BOOP portal.
                </p>
              </div>

              <table v-else class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-uppercase text-xxs font-weight-bolder">Licence Type</th>
                    <th class="text-uppercase text-xxs font-weight-bolder">License ID</th>
                    <th class="text-uppercase text-xxs font-weight-bolder">Status</th>
                    <th class="text-uppercase text-xxs font-weight-bolder">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in paginatedRows" :key="row.row_key" class="clickable-row" @click="openViewModal(row)">
                    <td>
                      <div class="d-flex flex-column">
                        <span class="text-sm font-weight-bold">{{ getFriendlyLicenseName(row.license_code) }}</span>
                        <span class="text-xs license-group-chip">
                          {{ getFriendlyGroupLabel(row.group) }}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div class="d-flex flex-column">
                        <span v-if="row.request_action === 'update' && row.effective_status_key === 'pending'"
                          class="text-sm font-weight-bold text-id-primary">
                          Current: {{ row.current_license_id || "-" }}
                        </span>
                        <span v-else class="text-sm font-weight-bold text-id-primary">
                          {{ row.current_license_id || row.requested_license_id || "-" }}
                        </span>
                        <span
                          v-if="row.request_action === 'update' && row.effective_status_key === 'pending' && row.requested_license_id"
                          class="text-xs text-id-secondary">
                          Requested: {{ row.requested_license_id }}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span class="badge badge-sm" :class="statusBadgeClass(row.effective_status_key)">
                        {{ row.effective_status_label }}
                      </span>
                    </td>
                    <td class="action-cell">
                      <div class="d-flex justify-content-start gap-2">
                        <button class="btn btn-link text-info px-2 mb-0" title="View" @click.stop="openViewModal(row)">
                          <i class="fas fa-eye"></i>
                        </button>
                        <button v-if="showUpdateAction(row)" class="btn btn-link text-primary px-2 mb-0" title="Update"
                          @click.stop="openUpdateModal(row)">
                          <i class="fas fa-edit"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="filteredRows.length > 0" class="d-flex justify-content-between align-items-center mt-3">
              <p class="mb-0 text-sm footer-count-text">
                Showing {{ pageStart }} to {{ pageEnd }} of {{ filteredRows.length }} entries
              </p>
              <div class="d-flex gap-2">
                <button class="btn btn-sm btn-outline-warning btn-strong-orange mb-0" :disabled="currentPage === 1"
                  @click="changePage(currentPage - 1)">
                  Previous
                </button>
                <button class="btn btn-sm btn-outline-warning btn-strong-orange mb-0" :disabled="currentPage === totalPages"
                  @click="changePage(currentPage + 1)">
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showCreateModal" class="modal-overlay" @click.self="closeCreateModal">
      <div class="modal-container">
        <div class="modal-card">
          <div class="modal-header">
            <h5 class="modal-title mb-0"><i class="fas fa-plus me-2"></i>Register New License</h5>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-md-4">
                <label class="form-label">License Group</label>
                <select class="form-control form-control-modern" v-model="createForm.group"
                  :disabled="isCreateSubmitting">
                  <option value="" disabled>Select group</option>
                  <option value="online">Online Betting Licences</option>
                  <option value="land">Landbased Licences</option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">License Type</label>
                <select class="form-control form-control-modern" v-model="createForm.license_code"
                  :disabled="isCreateSubmitting || !createForm.group">
                  <option value="" disabled>Select type</option>
                  <option v-for="item in createTypeOptions" :key="item.code" :value="item.code">
                    {{ formatOptionLabel(item) }}
                  </option>
                </select>
              </div>
              <div class="col-md-4">
                <label class="form-label">License ID</label>
                <input class="form-control form-control-modern" v-model.trim="createForm.license_id"
                  :disabled="isCreateSubmitting || !createForm.license_code" placeholder="Enter license ID" />
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-3 mb-2">
              <span class="text-sm text-secondary">Staged licenses: {{ createQueue.length }}</span>
            </div>
            <div v-if="createQueue.length" class="staged-box mb-3">
              <div v-for="(item, index) in createQueue" :key="`${item.license_code}-${index}`" class="staged-row">
                <span class="text-sm">{{ getFriendlyLicenseName(item.license_code) }} - {{ item.license_id }}</span>
                <button class="btn btn-link text-danger btn-sm mb-0" @click="removeCreateItem(index)">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2">
              <button class="btn btn-outline-secondary mb-0" @click="closeCreateModal"
                :disabled="isCreateSubmitting">Cancel</button>
              <button class="btn btn-outline-success mb-0" @click="addAnotherCreate" :disabled="isCreateSubmitting">
                <i class="fas fa-plus me-2"></i>
                Add Another License
              </button>
              <button class="btn btn-success mb-0" @click="saveAndSubmitCreates" :disabled="isCreateSubmitting">
                <i v-if="isCreateSubmitting" class="fas fa-spinner fa-spin me-2"></i>
                <i v-else class="fas fa-paper-plane me-2"></i>
                Save & Submit ({{ createQueue.length + (isCreateFormFilled ? 1 : 0) }})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showUpdateModal" class="modal-overlay" @click.self="closeUpdateModal">
      <div class="modal-container">
        <div class="modal-card">
          <div class="modal-header">
            <h5 class="modal-title mb-0"><i class="fas fa-edit me-2"></i>Update License</h5>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">License to Update</label>
                <select class="form-control form-control-modern" v-model="updateForm.license_record_id"
                  :disabled="isUpdateSubmitting" @change="syncUpdateTarget">
                  <option value="" disabled>Select license</option>
                  <option v-for="item in updatableRows" :key="item.license_record_id" :value="item.license_record_id">
                    {{ getFriendlyLicenseName(item.license_code) }} - {{ item.current_license_id }}
                  </option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label">New License ID</label>
                <input class="form-control form-control-modern" v-model.trim="updateForm.new_license_id"
                  :disabled="isUpdateSubmitting || !updateForm.license_record_id" />
              </div>
              <div class="col-12">
                <label class="form-label">Reason for Update</label>
                <textarea class="form-control form-control-modern" rows="3" v-model.trim="updateForm.reason"
                  :disabled="isUpdateSubmitting || !updateForm.license_record_id"></textarea>
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-3 mb-2">
              <span class="text-sm text-secondary">Staged updates: {{ updateQueue.length }}</span>
            </div>
            <div v-if="updateQueue.length" class="staged-box mb-3">
              <div v-for="(item, index) in updateQueue" :key="`${item.license_record_id}-${index}`" class="staged-row">
                <span class="text-sm">{{ getFriendlyLicenseName(item.license_code) }}: {{ item.current_license_id }} -> {{ item.new_license_id
                  }}</span>
                <button class="btn btn-link text-danger btn-sm mb-0" @click="removeUpdateItem(index)">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2">
              <button class="btn btn-outline-secondary mb-0" @click="closeUpdateModal"
                :disabled="isUpdateSubmitting">Cancel</button>
              <button class="btn btn-outline-success mb-0" @click="addAnotherUpdate" :disabled="isUpdateSubmitting">
                <i class="fas fa-plus me-2"></i>
                Update Another
              </button>
              <button class="btn btn-success mb-0" @click="saveAndSubmitUpdates" :disabled="isUpdateSubmitting">
                <i v-if="isUpdateSubmitting" class="fas fa-spinner fa-spin me-2"></i>
                <i v-else class="fas fa-paper-plane me-2"></i>
                Update & Submit ({{ updateQueue.length + (isUpdateFormFilled ? 1 : 0) }})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showViewModal" class="modal-overlay" @click.self="closeViewModal">
      <div class="modal-container modal-sm">
        <div class="modal-card">
          <div class="modal-header">
            <h5 class="modal-title mb-0"><i class="fas fa-eye me-2"></i>License Details</h5>
          </div>
          <div class="modal-body" v-if="selectedViewRow">
            <div class="detail-panel">
              <div class="detail-row"><strong>Type:</strong> {{ getFriendlyLicenseName(selectedViewRow.license_code) }}</div>
              <div class="detail-row"><strong>Group:</strong> {{ getFriendlyGroupLabel(selectedViewRow.group) }}</div>
              <template v-if="selectedViewRow.request_action === 'update' && selectedViewRow.effective_status_key === 'pending'">
                <div class="detail-row"><strong>Old ID:</strong> {{ selectedViewRow.current_license_id || "-" }}</div>
                <div class="detail-row"><strong>New ID:</strong> {{ selectedViewRow.requested_license_id || "-" }}</div>
              </template>
              <template v-else>
                <div class="detail-row" v-if="selectedViewRow.current_license_id"><strong>Current ID:</strong> {{ selectedViewRow.current_license_id }}</div>
                <div class="detail-row" v-if="selectedViewRow.requested_license_id"><strong>Requested ID:</strong> {{ selectedViewRow.requested_license_id }}
                </div>
              </template>
              <div class="detail-row"><strong>Status:</strong> {{ selectedViewRow.effective_status_label }}</div>
              <div class="detail-row"><strong>Submitted:</strong> {{ formatDate(selectedViewRow.submitted_at) }}</div>
              <div class="detail-row" v-if="selectedViewRow.reason"><strong>Reason:</strong> {{ selectedViewRow.reason
                }}</div>
              <div class="detail-row" v-if="selectedViewRow.rejection_reason"><strong>Rejection Reason:</strong> {{
                selectedViewRow.rejection_reason }}</div>
            </div>
            <div class="d-flex justify-content-end mt-3 gap-2">
              <button v-if="showUpdateAction(selectedViewRow)" class="btn btn-primary mb-0" @click="openUpdateFromView">
                <i class="fas fa-edit me-2"></i>
                Update License
              </button>
              <button class="btn btn-outline-secondary mb-0" @click="closeViewModal">Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const FALLBACK_OPTIONS = [
  { code: "BM_ONLINE", name: "General Betting License Online", group: "online" },
  { code: "CS_ONLINE", name: "Casino Operating License Online", group: "online" },
  { code: "BG_ONLINE", name: "Bingo Betting License Online", group: "online" },
  { code: "BM_LAND", name: "General Betting License Land", group: "land" },
  { code: "CS_LAND", name: "Casino Operating License Land", group: "land" },
  { code: "BMTOL", name: "Gaming Machines Landbased (BMTOL)", group: "land" },
];

export default {
  name: "LicensesPage",
  data() {
    return {
      isLoading: false,
      isCreateSubmitting: false,
      isUpdateSubmitting: false,
      rawRows: [],
      displayRows: [],
      licenseOptions: [...FALLBACK_OPTIONS],
      searchQuery: "",
      statusFilter: "all",
      currentPage: 1,
      itemsPerPage: 10,
      showCreateModal: false,
      showUpdateModal: false,
      showViewModal: false,
      selectedViewRow: null,
      createForm: { group: "", license_code: "", license_id: "" },
      createQueue: [],
      updateForm: {
        license_record_id: "",
        license_code: "",
        group: "",
        current_license_id: "",
        new_license_id: "",
        reason: "",
      },
      updateQueue: [],
    };
  },
  computed: {
    createTypeOptions() {
      return this.licenseOptions.filter((item) => item.group === this.createForm.group);
    },
    filteredRows() {
      let list = [...this.displayRows];
      if (this.statusFilter !== "all") {
        list = list.filter((r) => r.effective_status_key === this.statusFilter);
      }
      if (this.searchQuery) {
        const key = this.searchQuery.toLowerCase();
        list = list.filter((r) =>
          [
            r.license_code,
            this.getFriendlyLicenseName(r.license_code),
            this.getFriendlyGroupLabel(r.group),
            r.current_license_id,
            r.requested_license_id,
            r.effective_status_label,
          ]
            .join(" ")
            .toLowerCase()
            .includes(key),
        );
      }
      return list;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.itemsPerPage));
    },
    paginatedRows() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredRows.slice(start, start + this.itemsPerPage);
    },
    pageStart() {
      return this.filteredRows.length === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    pageEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredRows.length);
    },
    isCreateFormFilled() {
      return !!(this.createForm.group || this.createForm.license_code || this.createForm.license_id);
    },
    isUpdateFormFilled() {
      return !!(this.updateForm.license_record_id || this.updateForm.new_license_id || this.updateForm.reason);
    },
    updatableRows() {
      return this.displayRows.filter((row) => row.can_update);
    },
  },
  watch: {
    filteredRows() {
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    },
  },
  created() {
    this.loadPageData();
  },
  methods: {
    async loadPageData() {
      this.isLoading = true;
      try {
        await Promise.all([this.fetchLicenseOptions(), this.fetchLicenseRows()]);
      } finally {
        this.isLoading = false;
      }
    },
    async fetchLicenseOptions() {
      try {
        const response = await axios.get("/license-options/");
        if (Array.isArray(response.data) && response.data.length > 0) {
          this.licenseOptions = response.data;
        }
      } catch {
        this.licenseOptions = [...FALLBACK_OPTIONS];
      }
    },
    async fetchLicenseRows() {
      const response = await axios.get("/operator-licenses/");
      this.rawRows = Array.isArray(response.data) ? response.data : [];
      this.displayRows = this.buildDisplayRows(this.rawRows);
    },
    buildDisplayRows(rows) {
      const activeRows = rows.filter((r) => !r.request_id && Number(r.status) !== 0);
      const requestRows = rows
        .filter((r) => !!r.request_id && Number(r.status) !== 0)
        .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));

      const tableRows = [];
      const byLicenseRecordId = new Map();

      activeRows.forEach((r) => {
        const item = {
          row_key: `active-${r.license_record_id || r.row_key}`,
          license_record_id: r.license_record_id || null,
          group_label: this.getFriendlyGroupLabel(r.group),
          group: r.group,
          license_code: r.license_code,
          current_license_id: r.license_id || null,
          requested_license_id: null,
          request_action: null,
          request_status: null,
          request_id: null,
          reason: r.reason || null,
          rejection_reason: r.rejection_reason || null,
          submitted_at: null,
          updated_at: r.updated_at || r.created_at,
          effective_status_key: Number(r.status) === 0 ? "deleted" : "approved",
          effective_status_label: Number(r.status) === 0 ? "Deleted" : "Approved",
          can_update: Number(r.status) === 2,
        };
        tableRows.push(item);
        if (item.license_record_id) {
          byLicenseRecordId.set(item.license_record_id, item);
        }
      });

      requestRows.forEach((req) => {
        // For update/delete requests that are already approved, keep only the
        // active/current record in the operator table view.
        if (Number(req.status) === 2 && req.action !== "create") return;

        if (req.action === "create") {
          const hasMatchingActive = tableRows.some(
            (row) =>
              row.license_code === req.license_code &&
              row.current_license_id === req.license_id &&
              row.effective_status_key !== "deleted",
          );
          if (hasMatchingActive) return;

          tableRows.push({
            row_key: `request-${req.request_id}`,
            license_record_id: req.license_record_id || null,
            group_label: this.getFriendlyGroupLabel(req.group),
            group: req.group,
            license_code: req.license_code,
            current_license_id: null,
            requested_license_id: req.license_id || null,
            request_action: req.action,
            request_status: Number(req.status),
            request_id: req.request_id,
            reason: req.reason || null,
            rejection_reason: req.rejection_reason || null,
            submitted_at: req.submitted_at || null,
            updated_at: req.updated_at || req.created_at,
            effective_status_key: this.mapStatusKey(req.status),
            effective_status_label: this.mapStatusLabel(req.status),
            can_update: false,
          });
          return;
        }

        const target = byLicenseRecordId.get(req.license_record_id);
        if (!target || target.request_status === 1) return;

        target.requested_license_id = req.action === "delete" ? null : req.license_id;
        target.request_action = req.action;
        target.request_status = Number(req.status);
        target.request_id = req.request_id;
        target.reason = req.reason || null;
        target.rejection_reason = req.rejection_reason || null;
        target.submitted_at = req.submitted_at || null;
        target.updated_at = req.updated_at || target.updated_at;

        if (Number(req.status) === 1) {
          target.effective_status_key = "pending";
          target.effective_status_label = "Pending Review";
          target.can_update = false;
        } else if (Number(req.status) === 3) {
          // Rejected update/delete request should not change the base licence
          // status shown in the operator table.
          if (req.action === "create") {
            target.effective_status_key = "rejected";
            target.effective_status_label = "Rejected";
          }
          target.can_update = true;
        }
      });

      return tableRows.sort((a, b) => {
        const aPending = a.effective_status_key === "pending" ? 1 : 0;
        const bPending = b.effective_status_key === "pending" ? 1 : 0;
        if (aPending !== bPending) return bPending - aPending;
        return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
      });
    },
    mapStatusKey(status) {
      if (Number(status) === 1) return "pending";
      if (Number(status) === 2) return "approved";
      if (Number(status) === 3) return "rejected";
      return "deleted";
    },
    mapStatusLabel(status) {
      if (Number(status) === 1) return "Pending Review";
      if (Number(status) === 2) return "Approved";
      if (Number(status) === 3) return "Rejected";
      return "Deleted";
    },
    getFriendlyGroupLabel(group) {
      if (group === "online") return "Online Betting Licences";
      if (group === "land") return "Landbased Licences";
      return "Licence Group";
    },
    getFriendlyLicenseName(code) {
      const names = {
        BM_ONLINE: "General Betting License Online",
        BM_LAND: "General Betting License Land",
        CS_ONLINE: "Casino Operating License Online",
        CS_LAND: "Casino Operating License Land",
        BG_ONLINE: "Bingo Betting License Online",
        BMTOL: "Gaming Machines Landbased License",
      };
      return names[code] || code;
    },
    formatOptionLabel(item) {
      if (!item) return "";
      return this.getFriendlyLicenseName(item.code);
    },
    openCreateModal() {
      this.showCreateModal = true;
      this.createQueue = [];
      this.createForm = { group: "", license_code: "", license_id: "" };
    },
    closeCreateModal() {
      this.showCreateModal = false;
      this.createQueue = [];
      this.createForm = { group: "", license_code: "", license_id: "" };
    },
    createFormPayload() {
      if (!this.createForm.group || !this.createForm.license_code || !this.createForm.license_id) return null;
      return {
        group: this.createForm.group,
        license_code: this.createForm.license_code,
        license_id: this.createForm.license_id,
      };
    },
    addAnotherCreate() {
      const payload = this.createFormPayload();
      if (!payload) {
        this.notify("Complete the current license form first.", "error");
        return;
      }
      this.createQueue.push(payload);
      this.createForm = { group: "", license_code: "", license_id: "" };
    },
    removeCreateItem(index) {
      this.createQueue.splice(index, 1);
    },
    async saveAndSubmitCreates() {
      const pending = [...this.createQueue];
      const current = this.createFormPayload();
      if (current) pending.push(current);
      if (pending.length === 0) {
        this.notify("Add at least one license to submit.", "error");
        return;
      }

      this.isCreateSubmitting = true;
      try {
        const requestIds = [];
        for (const item of pending) {
          const response = await axios.post("/operator-license-requests/", {
            action: "create",
            group: item.group,
            license_code: item.license_code,
            license_id: item.license_id,
          });
          if (response?.data?.id) requestIds.push(response.data.id);
        }
        await axios.post("/operator-license-requests/submit/", { request_ids: requestIds });
        this.notify(`${requestIds.length} license(s) submitted for review.`, "success");
        this.closeCreateModal();
        await this.fetchLicenseRows();
      } catch (error) {
        this.notify(error.response?.data?.error || "Failed to submit licenses.", "error");
      } finally {
        this.isCreateSubmitting = false;
      }
    },
    canRequestUpdate(row) {
      return !!row.can_update && !!row.license_record_id;
    },
    showUpdateAction(row) {
      if (!row.license_record_id) return false;
      if (row.effective_status_key === "approved") return true;
      if (row.request_action === "update" && row.effective_status_key === "pending") return true;
      return false;
    },
    openUpdateModal(row) {
      this.showUpdateModal = true;
      this.updateQueue = [];
      this.updateForm = {
        license_record_id: row?.license_record_id || "",
        license_code: row?.license_code || "",
        group: row?.group || "",
        current_license_id: row?.current_license_id || "",
        new_license_id: "",
        reason: "",
      };
    },
    closeUpdateModal() {
      this.showUpdateModal = false;
      this.updateQueue = [];
      this.updateForm = {
        license_record_id: "",
        license_code: "",
        group: "",
        current_license_id: "",
        new_license_id: "",
        reason: "",
      };
    },
    syncUpdateTarget() {
      const target = this.updatableRows.find((row) => String(row.license_record_id) === String(this.updateForm.license_record_id));
      if (!target) return;
      this.updateForm.license_code = target.license_code;
      this.updateForm.group = target.group;
      this.updateForm.current_license_id = target.current_license_id;
    },
    updateFormPayload() {
      if (!this.updateForm.license_record_id || !this.updateForm.new_license_id || !this.updateForm.reason) return null;
      return {
        license_record_id: this.updateForm.license_record_id,
        license_code: this.updateForm.license_code,
        group: this.updateForm.group,
        current_license_id: this.updateForm.current_license_id,
        new_license_id: this.updateForm.new_license_id,
        reason: this.updateForm.reason,
      };
    },
    addAnotherUpdate() {
      const payload = this.updateFormPayload();
      if (!payload) {
        this.notify("Select a license and fill new ID plus reason.", "error");
        return;
      }
      this.updateQueue.push(payload);
      this.updateForm = {
        license_record_id: "",
        license_code: "",
        group: "",
        current_license_id: "",
        new_license_id: "",
        reason: "",
      };
    },
    removeUpdateItem(index) {
      this.updateQueue.splice(index, 1);
    },
    async saveAndSubmitUpdates() {
      const pending = [...this.updateQueue];
      const current = this.updateFormPayload();
      if (current) pending.push(current);
      if (pending.length === 0) {
        this.notify("Add at least one update request.", "error");
        return;
      }

      this.isUpdateSubmitting = true;
      try {
        const requestIds = [];
        for (const item of pending) {
          const response = await axios.post("/operator-license-requests/", {
            action: "update",
            license_record_id: Number(item.license_record_id),
            license_code: item.license_code,
            group: item.group,
            license_id: item.new_license_id,
            reason: item.reason,
          });
          if (response?.data?.id) requestIds.push(response.data.id);
        }
        await axios.post("/operator-license-requests/submit/", { request_ids: requestIds });
        this.notify(`${requestIds.length} update request(s) submitted.`, "success");
        this.closeUpdateModal();
        await this.fetchLicenseRows();
      } catch (error) {
        this.notify(error.response?.data?.error || "Failed to submit update requests.", "error");
      } finally {
        this.isUpdateSubmitting = false;
      }
    },
    openViewModal(row) {
      this.selectedViewRow = row;
      this.showViewModal = true;
    },
    openUpdateFromView() {
      if (!this.selectedViewRow) return;
      const row = { ...this.selectedViewRow };
      this.closeViewModal();
      this.openUpdateModal(row);
    },
    closeViewModal() {
      this.showViewModal = false;
      this.selectedViewRow = null;
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
    },
    resetFilters() {
      this.searchQuery = "";
      this.statusFilter = "all";
      this.currentPage = 1;
    },
    statusBadgeClass(statusKey) {
      const map = {
        pending: "status-pill-pending",
        approved: "status-pill-approved",
        rejected: "status-pill-rejected",
        deleted: "status-pill-deleted",
      };
      return map[statusKey] || "status-pill-deleted";
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleString();
    },
    notify(message, type = "success") {
      const existingAlerts = document.querySelectorAll(".fixed-notification");
      existingAlerts.forEach((alert) => alert.remove());
      const notification = document.createElement("div");
      notification.className = `alert alert-${type === "error" ? "danger" : "success"} alert-dismissible fade show fixed-notification`;
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 9999;
        min-width: 300px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      `;
      notification.innerHTML = `
        <strong>${type === "error" ? "Error!" : "Success!"}</strong> ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (notification.parentNode) notification.remove();
      }, 3500);
    },
  },
};
</script>

<style scoped>
.form-control-modern {
  border: 1px solid #d7dee9;
  border-radius: 0.5rem;
  min-height: 42px;
  padding: 0.65rem 0.9rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #344767;
  background-color: #fff;
}

.form-control-modern:focus {
  border-color: #4caf50;
  box-shadow: 0 0 0 0.15rem rgba(76, 175, 80, 0.15);
}

select.form-control-modern {
  appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, #67748e 50%), linear-gradient(135deg, #67748e 50%, transparent 50%);
  background-position: calc(100% - 16px) calc(1em + 1px), calc(100% - 11px) calc(1em + 1px);
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  padding-right: 2rem;
  padding-left: 0.9rem;
}

.table thead th {
  color: #67748e;
  letter-spacing: 0.02em;
  border-bottom: 1px solid #e8edf5;
}

.table tbody tr:hover {
  background: #f8fafc;
}

.clickable-row {
  cursor: pointer;
}

.table tbody td {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.form-label {
  color: #5f6e8a !important;
  font-weight: 600;
}

.text-id-primary {
  color: #344767;
}

.text-id-secondary {
  color: #6c757d;
}

.license-group-chip {
  color: #b86e00;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.footer-count-text {
  color: #67748e;
}

.filter-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  top: 50%;
  right: 0.85rem;
  transform: translateY(-50%);
  color: #67748e;
  pointer-events: none;
}

.action-cell {
  min-width: 170px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-container {
  width: min(96vw, 780px);
}

.modal-sm {
  width: min(96vw, 520px);
}

.modal-card {
  background: #fff;
  border-radius: 0.75rem;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.2);
}

.modal-header {
  padding: 1rem 1rem 0.5rem;
  border-bottom: 1px solid #eef2f7;
}

.modal-body {
  padding: 1rem;
}

.staged-box {
  border: 1px solid #e8edf5;
  background: #f8fafc;
  border-radius: 0.5rem;
  max-height: 140px;
  overflow: auto;
}

.staged-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0.7rem;
  border-bottom: 1px solid #eef2f7;
}

.staged-row:last-child {
  border-bottom: none;
}

.detail-row {
  font-size: 0.88rem;
  margin-bottom: 0.45rem;
}

.detail-panel {
  border: 1px solid #eef2f7;
  border-radius: 0.5rem;
  padding: 0.85rem;
  background: #fbfcfe;
}

.status-pill-approved {
  background: #2e7d32 !important;
  color: #fff !important;
}

.status-pill-rejected {
  background: #e53935 !important;
  color: #fff !important;
}

.status-pill-pending {
  background: #cb7a00 !important;
  color: #fff !important;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.status-pill-deleted {
  background: #6c757d !important;
  color: #fff !important;
}

.btn-strong-orange {
  color: #c37400 !important;
  border-color: #d98b1a !important;
  font-weight: 700 !important;
}

.btn-strong-orange:hover {
  color: #fff !important;
  background: #c37400 !important;
  border-color: #c37400 !important;
}
</style>
